import React, { useEffect, useState } from 'react';
import type { FC } from 'react';
import { SelectInput, TextInput } from '@wix/cli-ui-kit';
import type { VariantOf } from 'variant';
import { fields, match, variant } from 'variant';
import type { ActionSchema } from '@wix/cli-app-definitions';
import { ComponentType } from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import {
  composeValidations,
  useValidation,
  uuidValidation,
} from '../../../../dev-center/components/validations/index.js';
import { useWixCliAstroBi } from '../../../../bi/index.js';
import { useExtensions } from '../../../extensions/useExtensions.js';

const NavigateToPageOption = variant({
  Page: fields<{ pageId: string }>(),
  Other: {},
});

type NavigateToPageOption = VariantOf<typeof NavigateToPageOption>;

export const NavigateToPage: FC<{
  onActionSelected: (action: ActionSchema) => void;
}> = ({ onActionSelected }) => {
  const bi = useWixCliAstroBi();
  const [pageId, setPageId] = useState<string>();
  const [shouldUseCustomPageId, setShouldUseCustomPageId] =
    useState<boolean>(false);
  const { getExtensions } = useExtensions();

  const dashboardPages = getExtensions(ComponentType.BACK_OFFICE_PAGE);

  const validatePageId = useValidation(
    composeValidations([
      [uuidValidation, 'Page id has to be in the format of a uuid'],
    ])
  );

  useEffect(() => {
    if (pageId) {
      onActionSelected({
        navigateToPage: {
          pageId,
        },
      });
    }
  }, [pageId, onActionSelected]);

  return (
    <>
      <SelectInput<NavigateToPageOption>
        label={'Choose a page the menu item will navigate to:'}
        info={
          'Choose from a list of available pages in your project or manually add a page from a different app'
        }
        options={[
          ...dashboardPages.map(({ compId, compData }) => ({
            key: compId,
            title: compData.backOfficePage.title,
            value: NavigateToPageOption.Page({ pageId: compId }),
          })),
          {
            key: 'other',
            title: 'Use a page from a different app',
            value: NavigateToPageOption.Other(),
          },
        ]}
        onSubmit={(option) => {
          bi.cliFlowStepAnswered({
            question: 'Choose a page the menu item will navigate to:',
            questionKey:
              'generate_command.dashboard_menu_plugin.what_to_do.navigate_to_page.choose_page',
            answer: option.title,
          });
          return match(option.value, {
            Page: ({ pageId }) => setPageId(pageId),
            Other: () => setShouldUseCustomPageId(true),
          });
        }}
      />
      {shouldUseCustomPageId && (
        <TextInput
          label={'What is the unique id of the page?'}
          onSubmit={(pageId) => {
            bi.cliFlowStepAnswered({
              question: 'What is the unique id of the page?',
              questionKey:
                'generate_command.dashboard_menu_plugin.what_to_do.navigate_to_page.choose_page.other.choose_page_id',
              answer: pageId,
            });
            setPageId(pageId);
          }}
          validate={validatePageId}
        />
      )}
    </>
  );
};
